/**
 * Structured key-terms card rendered above ContractSigningWidget for
 * product-scoped contracts. Sourced from contract metadata (not parsed
 * from the PDF) so the values are guaranteed to match the underlying
 * product schema even if the PDF rendering pipeline lags.
 *
 * Legal posture (#48): this card is supplementary to the PDF — the PDF
 * remains the authoritative document and the consent checkbox below the
 * signing widget continues to gate the actual signature. The summary
 * exists so the client cannot honestly claim "I didn't know the yield /
 * term / fee".
 */
import type { ProductTerms } from '../../types/contracts';
import {
  formatEarlyWithdrawal,
  formatManagementFee,
  formatRedemption,
  formatTerm,
} from '../../lib/redemptionTerms';
import { colors } from '../../styles/theme';
import { readerLocale } from '../../i18n/language';
import { RISK_LEVELS } from '../../lib/riskLevels';

interface Props {
  readonly terms: ProductTerms;
}

function formatMoney(amount: string | null, ccy: string | null): string {
  if (amount === null || !ccy) return '—';
  const n = Number.parseFloat(amount);
  if (!Number.isFinite(n)) return `${amount} ${ccy}`;
  return new Intl.NumberFormat(readerLocale(), {
    style: 'currency', currency: ccy, maximumFractionDigits: 2,
  }).format(n);
}

function formatRate(decimal: string | null): string {
  if (decimal === null) return '—';
  const n = Number.parseFloat(decimal);
  if (!Number.isFinite(n)) return '—';
  return `${(n * 100).toFixed(2)}% p.a.`;
}

export default function KeyTermsSummary({ terms }: Props): JSX.Element {
  const risk = terms.risk_level === null ? null : RISK_LEVELS[terms.risk_level];
  // Term, redemption and early redemption render through the shared
  // derivation (lib/redemptionTerms — audit #138): this is the PRE-SIGNING
  // screen, the last place a liquidity promise could sneak in.
  const earlyTerm = formatEarlyWithdrawal(terms);
  const redemption = formatRedemption(terms) ?? 'See the offering document';

  return (
    <section
      style={{
        border: `1px solid ${colors.border}`,
        borderRadius: 12,
        background: '#FAFBFF',
        padding: '16px 18px',
        marginBottom: 20,
      }}
      aria-labelledby="key-terms-heading"
    >
      <header
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          marginBottom: 12,
          flexWrap: 'wrap',
          gap: 8,
        }}
      >
        <h3
          id="key-terms-heading"
          style={{
            margin: 0,
            fontSize: 13,
            fontWeight: 700,
            color: colors.ink,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
          }}
        >
          Key terms · {terms.name}
        </h3>
        <span
          style={{
            fontSize: 12,
            fontWeight: 700,
            color: colors.muted,
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
          }}
        >
          From the contract — read the PDF in full
        </span>
      </header>

      {!terms.terms_available && (
        <p
          role="note"
          style={{ margin: '0 0 12px', fontSize: 12, color: colors.muted }}
        >
          Terms unavailable right now — the figures below could not be confirmed.
        </p>
      )}

      <dl
        style={{
          margin: 0,
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          rowGap: 10,
          columnGap: 16,
        }}
      >
        <TermRow label="Annual series result, for information" value={formatRate(terms.target_yield)} valueColor={colors.goldOnLight} />
        <TermRow label="Term" value={formatTerm(terms)} />
        <TermRow label="Redemption" value={redemption} />
        <TermRow
          label="Minimum contribution"
          value={formatMoney(terms.min_investment, terms.currency_code)}
        />
        <TermRow
          label="Risk profile"
          value={risk?.label ?? '—'}
          valueColor={risk?.color}
        />
        <TermRow
          label="Early redemption"
          value={earlyTerm.text}
          valueColor={earlyTerm.tone === 'danger' ? colors.danger : undefined}
        />
        {/* Audit #140: the fee is shown next to the yield it reduces —
            a real 0 renders as "None", never as a missing row. */}
        <TermRow label="Management fee" value={formatManagementFee(terms)} />
        <TermRow
          label="What you receive"
          value="Your share of what the series actually earns, for each term your capital completes; when a term ends, the next one starts by itself. Capital withdrawn before its term ends earns nothing for that term; the result of that term belongs to the holder of the series' voting shares, a QMI company. An early withdrawal charge, where the series has one, is a penalty for ending a term early and is paid to QMI, not kept by the series."
        />
      </dl>

      <p
        style={{
          margin: '14px 0 0',
          fontSize: 12,
          color: colors.muted,
          lineHeight: 1.5,
        }}
      >
        These are the headline economic terms — the PDF below is the
        authoritative document. Read the full PDF before signing.
      </p>
    </section>
  );
}

interface TermRowProps {
  label: string;
  value: string;
  valueColor?: string;
}

function TermRow({ label, value, valueColor }: Readonly<TermRowProps>): JSX.Element {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <dt
        style={{
          fontSize: 12,
          fontWeight: 500,
          color: colors.muted,
        }}
      >
        {label}
      </dt>
      <dd
        style={{
          margin: 0,
          fontSize: 14,
          fontWeight: 700,
          color: valueColor ?? colors.ink,
        }}
      >
        {value}
      </dd>
    </div>
  );
}
